import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { applyLocale, readLocale } from "./locale/locale.js";
import { createI18n } from "./locale/i18n.js";
import { I18nextProvider } from "react-i18next";
import type { i18n as I18n } from "i18next";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// NOTE: the stylesheets are <link>ed from index.html so they are render-blocking. Importing them here
// would defer styling until this bundle executes — which made the off-screen skip link flash visible.
import { App } from "./app/App.js";
import { ErrorProvider } from "./error/ErrorProvider.js";
import { readBootstrap } from "./tenant/bootstrap.js";
import { initAuth, type AuthSession } from "./auth/keycloak.js";
import { parseWebglPanel } from "./features/facilities/webgl-panel/webgl-panel.js";
import { WebglElementPanel } from "./features/facilities/webgl-panel/WebglElementPanel.js";
import {
  isWebglAuthCallbackDocument,
  parseWebglAuthCallback,
  relayWebglAuthCallback,
} from "./auth/webgl-auth-callback.js";

// Server state lives in TanStack Query; client/canvas state will use Zustand (ADR-001). Keeping the two separate
// avoids the classic mistake of caching server data in a global store and hand-rolling invalidation.
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false } },
});

/**
 * ── THE AUTH CALLBACK DOCUMENT, AND IT MUST BE THE FIRST THING THIS MODULE DECIDES (ADR-050 D5) ──
 *
 * When the WebGL panel signs in, it opens a popup that finishes Keycloak's auth-code flow and comes back HERE.
 * This document's whole job is to hand `code`/`state` to its opener and close. It must not mount `App` (global
 * subscriptions, and a second presence user beside the author's own Unity session — the reason ADR-044 D2 exists),
 * must not mount the panel, and must not touch `initAuth`.
 *
 * So the check is a fork around the entire boot rather than a branch inside `mount`: by the time anything below
 * has run, a wrong answer has already had its side effects.
 *
 * There is deliberately no adapter and no exchange in this document — the PKCE verifier lives in the opener, which
 * is what keeps the memory-only guarantee true and makes ADR-050 D6's "never retries itself" structural rather
 * than guarded. See `webgl-auth-callback.ts` for both reasons in full.
 *
 * A malformed or absent OAuth response relays NOTHING and simply leaves the window open, which is the honest
 * outcome: the opener is still waiting and will time out or notice the close, and the user can see the URL.
 */
if (isWebglAuthCallbackDocument(window.location.search)) {
  const message = parseWebglAuthCallback(window.location.search);
  if (message) relayWebglAuthCallback(message);
} else {
  const container = document.getElementById("root");
  if (!container) throw new Error("#root missing from index.html");

  // FAIL CLOSED, mirroring the edge: if the tenant bootstrap is absent the host was never resolved, so we must not
  // guess a tenant or render branded chrome.
  const tenant = readBootstrap();

  /**
   * Apply the resolved locale BEFORE the first render.
   *
   * `applyLocale` otherwise runs only when the menu changes, so a German browser — or a persisted "Deutsch" choice
   * after a reload — left `<html lang>` at the hardcoded "en" from index.html while the trigger displayed "DE".
   * Assistive tech takes pronunciation from that attribute, so German content was announced with an English voice:
   * exactly the failure the locale module exists to prevent, on every first visit.
   *
   * Unlike the THEME, this does not need a render-blocking script in <head> — `lang` affects speech, not paint, so
   * setting it here is early enough and costs no blocking fetch.
   *
   * `applyLocale` deliberately does NOT persist (ADR-047 D2). This line runs on every boot with an AUTO-RESOLVED
   * value, so persisting here would record a fallback as if it were the user's choice — pinning them to it forever,
   * since a stored value wins the chain. Only the picker records.
   *
   * The tenant's `defaultLocale` is the chain's last step; `tenant` is null on the fail-closed path, and passing
   * `undefined` there simply falls through to "en".
   */
  const locale = readLocale(tenant?.defaultLocale);
  applyLocale(locale);

  const root = createRoot(container);

  /**
   * The WebGL embed target (ADR-044), read ONCE at boot. Non-null ⇒ Unity is embedding us and this document is a
   * fragment of someone else's page, which changes two things below: what we mount, and whether we may redirect.
   */
  const webglPanel = parseWebglPanel(window.location.search);

  function mount(i18n: I18n, session: AuthSession | null, error?: string) {
    root.render(
      <StrictMode>
        {/* Outside QueryClientProvider only by convention — i18n has no dependency on either, and a translated
            string can appear in an error boundary's own message, so it must be the outermost of the three. */}
        <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <ErrorProvider>
            {/* The panel replaces `<App/>` rather than branching inside it (ADR-044 D2, plan panel). `App` runs
                `useMe`, `usePresence`, `useMediaJobs`, `useFacilityNavParams` and `useFacilities` unconditionally —
                by design, hooks-first — so an in-App branch would boot every global subscription for a chrome-less
                iframe fragment and register the author as a SECOND "online user" beside their own Unity session.
                Branching here keeps the providers the panel actually needs and skips all of that. */}
            {webglPanel ? (
              /* `signInTenant` is non-null ONLY inside a frame (ADR-050 D7): a top-level tab already runs the
                ordinary redirect flow above, so offering a popup there would be a second, worse way to do the
                same thing. The panel DISABLES the control rather than hiding it when this is null, so there is
                no state in which a sign-in button exists but cannot work. */
            <WebglElementPanel target={webglPanel} session={session} signInTenant={framed ? tenant : null} />
            ) : (
              <App tenant={tenant} session={session} authError={error} />
            )}
          </ErrorProvider>
        </QueryClientProvider>
        </I18nextProvider>
      </StrictMode>,
    );
  }

  // EMBED MODE NEVER AUTHENTICATES INTERACTIVELY (ADR-044 D6, plan panel — this was the High finding).
  //
  // `initAuth` ends in `kc.login()`, which navigates the WHOLE DOCUMENT ("nothing after this resolves in the current
  // document" — keycloak.ts). Inside a third-party iframe that either breaks on frame-ancestors or silently
  // authenticates somewhere nobody can see. A refusal screen rendered downstream of that redirect is decoration on a
  // page no one ever reaches, so the guard has to live HERE, before the call.
  //
  // The consequence is stated rather than hidden: tokens are memory-only by policy (auth-tenancy.md §5) and `kc.init()`
  // is bare — no `check-sso` — so a freshly created iframe document has no token and no way to obtain one until the
  // Unity handshake lands (item 4, ADR-003 §C.3). Until then the panel correctly shows its not-signed-in state inside a
  // real embed, and is exercised end-to-end by opening the same URL as an ordinary top-level page while signed in.
  // FRAMED, specifically — not merely "on the webgl URL" (diff panel). The first version skipped `initAuth` for EVERY
  // webgl URL, which silently contradicted the paragraph above: opening the route as an ordinary top-level page could
  // then never obtain a session either, so the panel's content state was unreachable in ANY browser and the verification
  // path this comment promises did not exist. The thing we must never do is redirect someone else's FRAME; a top-level
  // tab is ours to redirect, and is how this route is exercised until the Unity handshake lands.
  const framed = window.self !== window.top;

  // Keycloak MUST initialise before React renders: init() consumes and rewrites the OIDC callback URL, so mounting
  // routes first would briefly interpret the auth response as application state.
  // Paint FIRST, then authenticate. The Keycloak round-trip (two 302s + a PKCE exchange) would otherwise leave
  // #root empty, so the user sees a blank body instead of branded chrome — and it makes the "Signing in…" state
  // reachable on the real path rather than only in tests.
  //
  // THE CATALOGUE IS AWAITED, and that is a deliberate exception to the line above (ADR-048 Phase 2). Every visible
  // string comes from it, so mounting first would paint the raw key set and swap it — a wrong-LANGUAGE flash, the
  // same class of defect as the wrong-brand flash `theming.md` forbids, and far worse than a few extra milliseconds.
  // What waits is only `#root`: the tenant's brand tokens, background and `<title>` are already in the initial HTML
  // (vite.tenant-plugin), so the page is branded throughout. The fetch is one same-origin chunk for the ACTIVE
  // language only — the English catalogue is never downloaded by a German user (D2).
  //
  // If auth is skipped (embed mode, or no tenant) this is still the only mount, which is why it renders the shell
  // rather than returning early.
  createI18n(locale, tenant?.realm).then(
    (i18n) => {
      mount(i18n, null);

      // EMBED MODE NEVER AUTHENTICATES INTERACTIVELY — see the note below.
      if (tenant && !(webglPanel && framed)) {
        initAuth(tenant).then(
          (session) => {
            if (session) mount(i18n, session); // null ⇒ a login redirect is under way; leave the pending shell up
          },
          (err: unknown) => mount(i18n, null, err instanceof Error ? err.message : String(err)),
        );
      }
    },
    (err: unknown) => {
      // The catalogue is a static same-origin asset, so this is a deploy or network fault, not a user state. There
      // is no translated string to explain it with — by definition — so the message is a bare English literal.
      console.error("failed to load the message catalogue", err);
      container.textContent = "This application could not finish loading. Please reload the page.";
    },
  );

}
